import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";

/**
 * NudgePermissionModal - A calm, supportive modal for requesting notification permission
 * 
 * Matches the app's emotional tone - no urgency, guilt, or pressure.
 * Platform notes:
 * - iOS Safari: Does NOT support web push notifications (as of 2024, only works in PWA mode on iOS 16.4+)
 * - Android Chrome: Full support
 * - Desktop Chrome/Firefox/Edge: Full support
 * - Desktop Safari 16+: Supported
 */
export const NudgePermissionModal = ({ isOpen, onClose, onPermissionGranted, taskName }) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission === "granted") {
        onPermissionGranted();
      } else {
        // User denied - close modal without nagging
        onClose();
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      onClose();
    } finally {
      setIsRequesting(false);
    }
  };

  if (!isOpen) return null;

  // Check if notifications are supported
  const notificationsSupported = "Notification" in window && "serviceWorker" in navigator;
  
  // Check if we're on iOS (where web push has limitations)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const iosLimited = isIOS && !isStandalone;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X size={18} />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-[#E0F7FA] flex items-center justify-center">
              <Bell size={24} className="text-[#3DC4D0]" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-lg font-semibold text-[#27272A] text-center mb-2">
            A gentle hand on your shoulder
          </h2>

          {/* Description - calm, supportive copy */}
          <p className="text-sm text-[#52525B] text-center mb-2 leading-relaxed">
            If you drift away from this important task, we'll send you a quiet reminder.
          </p>
          
          <p className="text-xs text-[#A1A1AA] text-center mb-5">
            No pressure. Just a nudge to help you find your thread again.
          </p>

          {/* Task preview */}
          {taskName && (
            <div className="bg-[#FDFBF7] rounded-xl p-3 mb-5 border border-stone-100">
              <p className="text-xs text-[#71717A] mb-1">You'll be nudged about:</p>
              <p className="text-sm text-[#27272A] font-medium">{taskName}</p>
            </div>
          )}

          {/* Platform-specific messaging */}
          {iosLimited && (
            <p className="text-xs text-amber-600 text-center mb-4 bg-amber-50 rounded-lg p-2">
              Note: On iPhone, nudges work best when you add this app to your home screen.
            </p>
          )}

          {!notificationsSupported && (
            <p className="text-xs text-stone-500 text-center mb-4 bg-stone-50 rounded-lg p-2">
              Notifications aren't supported in this browser.
            </p>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 text-stone-500 hover:text-stone-700 font-medium text-sm transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleRequestPermission}
              disabled={isRequesting || !notificationsSupported}
              className="flex-1 bg-[#3DC4D0] text-white rounded-xl py-2.5 font-semibold text-sm hover:bg-[#2BA8B3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRequesting ? "..." : "Enable nudges"}
            </button>
          </div>

          {/* Trust note */}
          <p className="text-[10px] text-[#A1A1AA] text-center mt-4">
            We'll only nudge you about tasks you choose. No spam, ever.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NudgePermissionModal;
